import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import {
  BuildingOfficeIcon,
  ChevronLeftIcon,
  EllipsisVerticalIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

type ComplianceStatus = 'Compliant' | 'At risk' | 'Non-compliant';

type SiteDetailModel = {
  id: string;
  address: string;
  clientName: string;
  propertyType: 'Domestic' | 'Commercial';
  accessNotes: string;
  complianceStatus: ComplianceStatus;
};

const getComplianceBadgeClass = (status: ComplianceStatus) => {
  if (status === 'Compliant') return 'status-success';
  if (status === 'At risk') return 'status-warning';
  return 'status-danger';
};

export default function SiteDetail() {
  const navigate = useNavigate();
  useParams();

  const site = useMemo(() => {
    return null as SiteDetailModel | null;
  }, []);

  if (!site) {
    return (
      <div className="page-container">
        <div className="mb-6 border-b border-[#E6E6E4] pb-3">
          <div className="page-header">
            <div>
              <button
                type="button"
                onClick={() => navigate('/dashboard/sites')}
                className="inline-flex items-center text-xs font-medium text-[#686E6C] hover:text-[#383838]"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                Back to Sites
              </button>
              <h1 className="mt-2 text-2xl font-semibold text-[#191919] leading-tight">Site</h1>
              <p className="mt-1 text-xs font-normal text-[#515959]">This feature is not available yet.</p>
            </div>
          </div>
        </div>

        <EmptyState
          icon={BuildingOfficeIcon}
          title="No site data"
          description="Sites are not yet connected to real data."
          actionLabel="Back to Sites"
          onAction={() => navigate('/dashboard/sites')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="mb-6 border-b border-[#E6E6E4] pb-3">
        <div className="page-header">
          <div>
            <button
              type="button"
              onClick={() => navigate('/dashboard/sites')}
              className="inline-flex items-center text-xs font-medium text-[#686E6C] hover:text-[#383838]"
            >
              <ChevronLeftIcon className="w-4 h-4 mr-1" />
              Back to Sites
            </button>

            <h1 className="mt-2 text-2xl font-semibold text-[#191919] leading-tight">{site.address}</h1>
            <p className="mt-1 text-xs font-normal text-[#515959]">{site.clientName}</p>
          </div>

          <div className="header-actions">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
            >
              <PencilIcon className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
            >
              <EllipsisVerticalIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#191919]">Overview</h2>
            <span className={`status-badge ${getComplianceBadgeClass(site.complianceStatus)}`}>
              {site.complianceStatus}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="text-[11px] font-medium text-[#6B7280]">Address</p>
              <p className="mt-1 text-sm text-[#111827]">{site.address}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-[#6B7280]">Client</p>
              <p className="mt-1 text-sm text-[#111827]">{site.clientName}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-[#6B7280]">Property type</p>
              <p className="mt-1 text-sm text-[#111827]">{site.propertyType}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-[#6B7280]">Access notes</p>
              <p className="mt-1 text-sm text-[#111827]">{site.accessNotes}</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#191919]">Appliances</h2>
            <button
              type="button"
              className="btn btn-primary"
              disabled
            >
              Add appliance
            </button>
          </div>

          <div className="mt-4">
            <EmptyState
              icon={BuildingOfficeIcon}
              title="No appliances added yet"
              description="Appliances linked to this site will appear here."
              actionLabel="Add appliance"
              onAction={() => undefined}
            />
          </div>
        </div>

        <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
          <h2 className="text-sm font-semibold text-[#191919]">Jobs</h2>
          <div className="mt-4">
            <div className="table-container" style={{ padding: 0 }}>
              <div className="table-wrapper">
                <table className="transactions-table">
                  <thead className="table-header">
                    <tr>
                      <th className="col-description">Job</th>
                      <th className="col-date">Date</th>
                      <th className="col-payment">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={3} className="text-center py-8">
                        <div className="text-gray-500">
                          <p className="font-medium mb-1">No jobs linked yet</p>
                          <p className="text-sm">Jobs for this site will appear here once connected.</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
          <h2 className="text-sm font-semibold text-[#191919]">Certificates</h2>
          <div className="mt-4">
            <div className="table-container" style={{ padding: 0 }}>
              <div className="table-wrapper">
                <table className="transactions-table">
                  <thead className="table-header">
                    <tr>
                      <th className="col-description">Certificate</th>
                      <th className="col-date">Issued</th>
                      <th className="col-date">Expiry</th>
                      <th className="col-payment">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={4} className="text-center py-8">
                        <div className="text-gray-500">
                          <p className="font-medium mb-1">No certificates linked yet</p>
                          <p className="text-sm">Certificates for this site will appear here once connected.</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
